import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useDeletePost, useAdminPosts } from '../../hooks/use-admin';
import { formatDateShort } from '../../lib/format';
import { EmptyState, ErrorState, Loading } from '../../components/StatusStates';
import Pagination from '../../components/Pagination';
import { cn } from '../../lib/format';

const PAGE_SIZE = 15;

type StatusFilter = '' | 'published' | 'draft';

export default function AdminPosts() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusFilter>('');
  const postsQuery = useAdminPosts({ page, pageSize: PAGE_SIZE, status: status || undefined });
  const deleteMutation = useDeletePost();

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`确定删除文章「${title}」吗？此操作不可恢复。`)) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '删除失败');
    }
  };

  const filterTabs: Array<{ value: StatusFilter; label: string }> = [
    { value: '', label: '全部' },
    { value: 'published', label: '已发布' },
    { value: 'draft', label: '草稿' },
  ];

  return (
    <>
      <div className="toolbar">
        <div className="filter-bar" style={{ marginBottom: 0 }}>
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={cn('tag tag-clickable', status === tab.value && 'tag-active')}
              onClick={() => {
                setStatus(tab.value);
                setPage(1);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="toolbar-spacer" />
        <Link to="/admin/posts/new" className="btn btn-primary btn-sm">
          <Plus size={15} aria-hidden="true" />
          新建文章
        </Link>
      </div>

      <div className="table-panel">
        {postsQuery.isLoading ? (
          <Loading />
        ) : postsQuery.isError ? (
          <ErrorState onRetry={() => postsQuery.refetch()} />
        ) : (postsQuery.data?.items ?? []).length === 0 ? (
          <EmptyState title="暂无文章" description="点击右上角「新建文章」开始写作。" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>标题</th>
                <th>状态</th>
                <th>标签</th>
                <th>发布时间</th>
                <th>更新时间</th>
                <th style={{ textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {postsQuery.data?.items.map((post) => (
                <tr key={post.id}>
                  <td className="col-title">{post.title}</td>
                  <td>
                    <span className={post.status === 'published' ? 'badge badge-published' : 'badge badge-draft'}>
                      {post.status === 'published' ? '已发布' : '草稿'}
                    </span>
                  </td>
                  <td>{post.tags.join('、') || '—'}</td>
                  <td>{formatDateShort(post.publishedAt)}</td>
                  <td>{formatDateShort(post.updatedAt)}</td>
                  <td>
                    <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                      <Link
                        to={`/admin/posts/${post.id}/edit`}
                        className="btn btn-outline btn-sm"
                        aria-label={`编辑 ${post.title}`}
                      >
                        <Pencil size={13} aria-hidden="true" />
                        编辑
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => void handleDelete(post.id, post.title)}
                        disabled={deleteMutation.isPending}
                        aria-label={`删除 ${post.title}`}
                      >
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {postsQuery.data && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={postsQuery.data.total}
          onChange={setPage}
        />
      )}
    </>
  );
}
